import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

/**
 * Formats a date/time string in the shop's timezone
 * This is the SINGLE SOURCE OF TRUTH for all time formatting in the UI
 * 
 * CRITICAL: Always use this instead of toLocaleTimeString() to ensure
 * times are displayed in the shop's timezone, not the browser's timezone
 */

export type TimeFormat = 
  | 'short'      // "9:30 AM"
  | 'medium'     // "09:30 AM"
  | 'long'       // "9:30:00 AM"
  | '24h'        // "09:30"
  | '24h-short'  // "9:30"
  | 'numeric';   // "9:30" (no AM/PM, 12hr)

/**
 * Format a Date object or ISO string in the shop's timezone
 * 
 * @param dateInput - Date object or ISO string (UTC)
 * @param shopTimezone - Shop timezone string (e.g., "America/Chicago")
 * @param formatStyle - Time format style (default: 'short')
 * @returns Formatted time string in shop timezone
 */
export function formatShopTime(
  dateInput: Date | string | null | undefined,
  shopTimezone: string,
  formatStyle: TimeFormat = 'short'
): string {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      console.warn('[formatShopTime] Invalid date input:', dateInput);
      return '';
    }
    
    // Convert UTC to shop timezone
    const zonedDate = toZonedTime(date, shopTimezone);
    
    // Format based on style
    switch (formatStyle) {
      case 'short':
        return format(zonedDate, 'h:mm a');
      case 'medium':
        return format(zonedDate, 'hh:mm a');
      case 'long':
        return format(zonedDate, 'h:mm:ss a');
      case '24h':
        return format(zonedDate, 'HH:mm');
      case '24h-short':
        return format(zonedDate, 'H:mm');
      case 'numeric':
        return format(zonedDate, 'h:mm');
      default:
        return format(zonedDate, 'h:mm a');
    }
  } catch (error) {
    console.error('[formatShopTime] Error formatting time:', error, { dateInput, shopTimezone });
    return '';
  }
}

/**
 * Format a time-only string (HH:mm) for display
 * Used for shift times, break times, etc. that are stored as time strings
 * 
 * @param timeString - Time in HH:mm format (e.g., "09:30", "14:00")
 * @param formatStyle - Time format style (default: 'short')
 * @returns Formatted time string (e.g., "9:30 AM", "2:00 PM")
 */
export function formatTimeString(
  timeString: string | null | undefined,
  formatStyle: TimeFormat = 'short'
): string {
  if (!timeString) return '';
  
  try {
    // Parse the time string by creating a date on a fixed reference date
    // This is timezone-agnostic since we're only dealing with the time portion
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('[formatTimeString] Invalid time string:', timeString);
      return '';
    }
    
    // Create a reference date with the time
    const refDate = new Date(2000, 0, 1, hours, minutes, 0);
    
    switch (formatStyle) {
      case 'short':
        return format(refDate, 'h:mm a');
      case 'medium':
        return format(refDate, 'hh:mm a');
      case 'long':
        return format(refDate, 'h:mm:ss a');
      case '24h':
        return format(refDate, 'HH:mm');
      case '24h-short':
        return format(refDate, 'H:mm');
      case 'numeric':
        return format(refDate, 'h:mm');
      default:
        return format(refDate, 'h:mm a');
    }
  } catch (error) {
    console.error('[formatTimeString] Error formatting time:', error, { timeString });
    return '';
  }
}

/**
 * Get the browser's detected timezone
 * Used as a fallback when shop timezone is not available
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Fallback to a neutral timezone if browser API fails
    return 'America/Chicago';
  }
}
